import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { isMissingSupabaseConfig } from "./lib/supabase.ts";
import { initSentry } from "./lib/sentry.ts";
import { registerServiceWorker } from "./lib/registerServiceWorker.ts";
import { PostHogProvider } from "posthog-js/react";
import type { PostHog } from "posthog-js";

// Initialise Sentry before React renders — ensures all errors are captured
initSentry();

registerServiceWorker();

// ─── PostHog configuration ────────────────────────────────────────────────────
// PostHog is initialised via <PostHogProvider> below.
// If VITE_POSTHOG_KEY is not set, the provider is omitted and the app runs
// without analytics (all trackEvent / identifyUser calls are safe no-ops).

const posthogKey  = import.meta.env.VITE_POSTHOG_KEY  as string | undefined;
const posthogHost = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? "https://us.i.posthog.com";

const posthogOptions = {
  api_host: posthogHost,

  // Autocapture
  capture_pageview:  true,   // Track $pageview on every route change
  capture_pageleave: true,   // Track $pageleave on navigation away

  // Privacy
  ip: false,                        // Do not capture IP address
  disable_session_recording: true,  // Session replay off by default

  loaded: (ph: PostHog) => {
    const forceEnable = import.meta.env.VITE_POSTHOG_FORCE_ENABLE === "true";
    if (import.meta.env.DEV && !forceEnable) {
      // Disable capturing in dev to avoid polluting production data.
      // To enable locally: set VITE_POSTHOG_FORCE_ENABLE=true in .env
      ph.opt_out_capturing();
      console.info("[Analytics] PostHog loaded — capturing disabled in dev mode");
      console.info("[Analytics] Set VITE_POSTHOG_FORCE_ENABLE=true to enable locally");
    } else if (import.meta.env.DEV && forceEnable) {
      console.info("[Analytics] PostHog loaded — dev opt-in active");
    }
  },
};

// ─── Setup Required Screen ────────────────────────────────────────────────────
// Shown when Supabase credentials have not yet been configured.
// This prevents a white-screen crash and guides developers to the README.

if (isMissingSupabaseConfig) {
  const root = document.getElementById("root")!;
  root.innerHTML = `
    <div style="
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #0f172a;
      font-family: system-ui, -apple-system, sans-serif;
      padding: 2rem;
    ">
      <div style="
        background: #1e293b;
        border: 1px solid #334155;
        border-radius: 12px;
        padding: 2.5rem 3rem;
        max-width: 560px;
        width: 100%;
        color: #e2e8f0;
      ">
        <div style="display:flex;align-items:center;gap:0.75rem;margin-bottom:1.5rem;">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#6366f1" stroke-width="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          <h1 style="font-size:1.4rem;font-weight:700;margin:0;color:#f1f5f9;">
            Strata Guardian — Setup Required
          </h1>
        </div>

        <p style="color:#94a3b8;margin:0 0 1.5rem;line-height:1.6;">
          The app needs Supabase credentials to start. Follow these steps:
        </p>

        <ol style="color:#cbd5e1;line-height:2;padding-left:1.25rem;margin:0 0 1.5rem;">
          <li>
            Copy <code style="background:#0f172a;padding:2px 6px;border-radius:4px;color:#a5b4fc;">.env.example</code>
            to <code style="background:#0f172a;padding:2px 6px;border-radius:4px;color:#a5b4fc;">.env</code>
          </li>
          <li>
            Go to your
            <a href="https://supabase.com/dashboard" target="_blank" rel="noreferrer"
               style="color:#818cf8;text-decoration:none;">Supabase Dashboard</a>
            → Project Settings → API
          </li>
          <li>
            Copy <strong>Project URL</strong> →
            <code style="background:#0f172a;padding:2px 6px;border-radius:4px;color:#a5b4fc;">VITE_SUPABASE_URL</code>
          </li>
          <li>
            Copy <strong>anon / public key</strong> →
            <code style="background:#0f172a;padding:2px 6px;border-radius:4px;color:#a5b4fc;">VITE_SUPABASE_ANON_KEY</code>
          </li>
          <li>Save <code style="background:#0f172a;padding:2px 6px;border-radius:4px;color:#a5b4fc;">.env</code>
            and restart the dev server (<code style="background:#0f172a;padding:2px 6px;border-radius:4px;color:#a5b4fc;">npm run dev</code>)
          </li>
        </ol>

        <div style="background:#0f172a;border:1px solid #1e3a5f;border-radius:8px;padding:1rem 1.25rem;font-size:0.85rem;color:#7dd3fc;">
          📖 See <strong>README.md</strong> → "Quick Start" for full setup instructions including
          database schema, Edge Functions, and Stripe configuration.
        </div>
      </div>
    </div>
  `;
} else {
  // Wrap with PostHogProvider only when key is present.
  // Without a key the provider is omitted and the app runs without analytics.
  const appTree = posthogKey ? (
    <PostHogProvider apiKey={posthogKey} options={posthogOptions}>
      <App />
    </PostHogProvider>
  ) : (
    <App />
  );

  createRoot(document.getElementById("root")!).render(appTree);
}
